import { api } from './client';

/**
 * Cartões de crédito salvos (tokenizados no cofre do provedor).
 *
 * O que a Bow guarda é só o token opaco + bandeira, 4 últimos dígitos e
 * validade — nunca o número, o CVV ou o nome do titular (checklist PCI §1).
 * Por isso `SavedCard` não tem, e nunca deve ganhar, campo de PAN.
 *
 * Endpoints atrás da flag `card_save_enabled` no backend. Enquanto ela
 * estiver desligada (ou o backend for anterior ao cartão), a listagem
 * responde 404/403 — quem chama trata como "recurso ainda não disponível",
 * não como erro (ver `isCardsUnavailable`).
 */

export type CardBrand = 'mastercard' | 'visa' | 'elo' | 'amex' | 'hiper' | (string & {});

export interface SavedCard {
  id: string;
  brand: CardBrand | null;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  created_at: string;
  last_used_at?: string | null;
}

export const cardsApi = {
  list: () => api.get<SavedCard[]>('/payments/cards'),
  remove: (cardId: string) => api.del<void>(`/payments/cards/${cardId}`),
  setDefault: (cardId: string) =>
    api.patch<SavedCard>(`/payments/cards/${cardId}`, { is_default: true })
};

/**
 * `true` quando a falha significa "cartão ainda não existe neste backend"
 * em vez de "deu erro". Cobre backend sem a rota (404), flag desligada
 * (403/404 com code próprio) e método não permitido.
 */
export function isCardsUnavailable(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  const code = (err as { code?: string } | null)?.code;
  return status === 404 || status === 405 || code === 'card_disabled';
}

/** `08/29` a partir de exp_month/exp_year. */
export function formatCardExpiry(card: Pick<SavedCard, 'exp_month' | 'exp_year'>): string {
  const mm = String(card.exp_month).padStart(2, '0');
  const yy = String(card.exp_year % 100).padStart(2, '0');
  return `${mm}/${yy}`;
}

const BRAND_LABEL: Record<string, string> = {
  mastercard: 'Mastercard',
  visa: 'Visa',
  elo: 'Elo',
  amex: 'American Express',
  hiper: 'Hipercard'
};

/** `Mastercard ···· 4412` — o rótulo que o cliente lê na lista. */
export function formatCardLabel(card: Pick<SavedCard, 'brand' | 'last4'>): string {
  const brand = card.brand ? (BRAND_LABEL[card.brand] ?? card.brand) : 'Cartão';
  return `${brand} ···· ${card.last4}`;
}
